import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HiOutlineMail, HiOutlinePhone, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import { useAuth, authErrorMessage } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import PWAInstallPrompt from '../../components/common/PWAInstallPrompt';

const Login = () => {
  const [mode, setMode] = useState('phone');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!identifier) {
      setError(mode === 'phone' ? 'ফোন নম্বর দিন।' : 'ইমেইল দিন।');
      return;
    }
    if (!password) {
      setError('পাসওয়ার্ড দিন।');
      return;
    }
    setLoading(true);
    try {
      const data = await login(identifier, password);
      navigate(data.user.role === 'admin' ? '/admin/dashboard' : '/worker/dashboard');
    } catch (err) {
      // Use the contextual message helper so we get a clean Bengali label
      // (covers wrong password, missing user, network down, server outage)
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const isPhone = mode === 'phone';

  return (
    <div className="min-h-screen flex items-stretch bg-ink-50 dark:bg-ink-900">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-brand relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
        <div className="relative text-white max-w-md">
          <div className="text-7xl mb-6">🔧</div>
          <h1 className="text-4xl font-bold mb-4 leading-tight">প্লাম্বার সিস্টেম</h1>
          <p className="text-lg text-white/90 mb-8">
            ওয়ার্কার, হাজিরা, বেতন — সব এক জায়গায়। সহজ, দ্রুত, নির্ভরযোগ্য।
          </p>
          <div className="space-y-3">
            {['রিয়েল-টাইম হাজিরা ট্র্যাকিং', 'স্বয়ংক্রিয় বেতন গণনা', 'অগ্রিম ম্যানেজমেন্ট', 'ফোন নম্বর দিয়ে লগইন'].map((feat, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">✓</div>
                <span className="text-white/90">{feat}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile brand */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-brand flex items-center justify-center text-white text-2xl shadow-pop">🔧</div>
              <h1 className="text-2xl font-bold text-ink-900 dark:text-white">প্লাম্বার সিস্টেম</h1>
            </div>
          </div>

          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 dark:text-white">{t('login')}</h2>
            <p className="text-sm text-ink-500 dark:text-ink-400 mt-2">{t('signInToAccount')}</p>
          </div>

          <div className="card shadow-card">
            {/* Tab switcher */}
            <div className="flex p-1 mb-5 bg-ink-100 dark:bg-ink-900/40 rounded-xl">
              {[
                { v: 'phone', label: 'ফোন', icon: HiOutlinePhone },
                { v: 'email', label: 'ইমেইল', icon: HiOutlineMail },
              ].map(({ v, label, icon: Icon }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => { setMode(v); setIdentifier(''); setError(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all ${
                    mode === v
                      ? 'bg-white dark:bg-ink-800 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'text-ink-500 dark:text-ink-400 hover:text-ink-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {error && (
                <div
                  role="alert"
                  className="p-3 bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 rounded-xl text-sm flex items-start gap-2 border border-rose-200 dark:border-rose-800 animate-slide-up"
                >
                  <span className="text-base flex-shrink-0 mt-0.5">⚠️</span>
                  <span className="flex-1 leading-snug">{error}</span>
                  <button
                    type="button"
                    onClick={() => setError('')}
                    aria-label="Dismiss error"
                    className="text-rose-400 hover:text-rose-700 dark:hover:text-rose-200 px-1"
                  >
                    ✕
                  </button>
                </div>
              )}

              <div>
                <label className="input-label">{isPhone ? 'ফোন নম্বর' : 'ইমেইল'} <span className="text-rose-500">*</span></label>
                <div className="relative">
                  {isPhone ? (
                    <>
                      <HiOutlinePhone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400" />
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        className="input-field pl-11 font-mono tracking-wide"
                        placeholder="01XXXXXXXXX"
                        autoComplete="tel"
                        maxLength={14}
                        required
                      />
                    </>
                  ) : (
                    <>
                      <HiOutlineMail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400" />
                      <input
                        type="email"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        className="input-field pl-11"
                        placeholder="you@example.com"
                        autoComplete="email"
                        required
                      />
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="input-label">{t('password')} <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <HiOutlineLockClosed className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400" />
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-11 pr-11"
                    placeholder="আপনার পাসওয়ার্ড"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-ink-400 hover:text-ink-700 dark:hover:text-ink-200"
                    tabIndex={-1}
                  >
                    {showPass ? <HiOutlineEyeOff className="w-5 h-5" /> : <HiOutlineEye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="btn-primary btn-lg w-full">
                {loading ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('loading')}
                  </>
                ) : (
                  t('login')
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-ink-600 dark:text-ink-400">
              {t('dontHaveAccount')}{' '}
              <Link to="/register" className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-semibold">
                {t('register')}
              </Link>
            </p>
          </div>
        </div>
      </div>
      {/* PWA install prompt — also visible on login page */}
      <PWAInstallPrompt />
    </div>
  );
};

export default Login;
